import {
  useEffect,
  useState,
} from 'react';

import {
  ArrowLeft,
  CheckCircle,
  Image,
  Package,
  Plus,
  Trash2,
  Users,
  XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import {
  imagesAPI,
  ordersAPI,
  usersAPI,
} from '../services/api';

export const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [brandImages, setBrandImages] = useState({});
  const [loading, setLoading] = useState(false);

  // Order form state
  const [orderForm, setOrderForm] = useState({
    week: '', client: '', product: '', variety: '', pallets: '',
    loadDate: '', vessel: '', etd: '', eta: '', status: 'Planned'
  });

  // Calculate week number from ETD date
  const getWeekNumber = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const days = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
    return 'W' + Math.ceil((days + startOfYear.getDay() + 1) / 7);
  };

  // Update week when ETD changes
  const handleOrderFormChange = (field, value) => {
    const newForm = { ...orderForm, [field]: value };
    if (field === 'etd' && value) {
      newForm.week = getWeekNumber(value);
    }
    setOrderForm(newForm);
  };

  // User form state
  const [userForm, setUserForm] = useState({ email: '', name: '', clientName: '' });

  // Brand image state
  const [selectedVessel, setSelectedVessel] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ordersData, usersData, imagesData] = await Promise.all([
        ordersAPI.getAll(),
        usersAPI.getAll(),
        imagesAPI.getAll()
      ]);
      setOrders(ordersData);
      setUsers(usersData);
      setBrandImages(imagesData);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    try {
      const newOrder = await ordersAPI.create({
        ...orderForm,
        pallets: Number(orderForm.pallets)
      });
      setOrders([...orders, newOrder]);
      setOrderForm({ week: '', client: '', product: '', variety: '', pallets: '', loadDate: '', vessel: '', etd: '', eta: '', status: 'Planned' });
    } catch (error) {
      alert('Failed to create order: ' + error.message);
    }
  };

  const handleDeleteOrder = async (id) => {
    if (!confirm('Delete this order?')) return;
    
    try {
      await ordersAPI.delete(id);
      setOrders(orders.filter(o => o.id !== id));
    } catch (error) {
      alert('Failed to delete order: ' + error.message);
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const newUser = await usersAPI.create({
        email: userForm.email,
        name: userForm.name,
        clientName: userForm.clientName
      });
      setUsers([...users, newUser]);
      setUserForm({ email: '', name: '', clientName: '' });
    } catch (error) {
      alert('Failed to create user: ' + error.message);
    }
  };

  const handleToggleApproval = async (id, currentApproval) => {
    try {
      await usersAPI.approve(id);
      setUsers(users.map(u => u.id === id ? { ...u, approved: !currentApproval } : u));
    } catch (error) {
      alert('Failed to update approval: ' + error.message);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!confirm('Delete this user?')) return;
    
    try {
      await usersAPI.delete(id);
      setUsers(users.filter(u => u.id !== id));
    } catch (error) {
      alert('Failed to delete user: ' + error.message);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const newImage = await imagesAPI.upload({ vessel: selectedVessel, image_data: ev.target.result });
          const updated = { ...brandImages };
          if (!updated[selectedVessel]) updated[selectedVessel] = [];
          updated[selectedVessel].push(newImage);
          setBrandImages(updated);
        } catch (error) {
          alert('Failed to upload image: ' + error.message);
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleDeleteImage = async (imageId, vessel) => {
    try {
      await imagesAPI.delete(imageId);
      const updated = { ...brandImages };
      updated[vessel] = updated[vessel].filter(img => img.id !== imageId);
      if (updated[vessel].length === 0) delete updated[vessel];
      setBrandImages(updated);
    } catch (error) {
      alert('Failed to delete image: ' + error.message);
    }
  };

  const vessels = [...new Set(orders.map(o => o.vessel))].sort();
  useEffect(() => {
    if (vessels.length > 0 && !selectedVessel) setSelectedVessel(vessels[0]);
  }, [vessels]);

  return (
    <div className="admin-page">
      <header className="dashboard-header">
        <div className="brand">
          <div className="brand-logo">EG</div>
          <h1>Admin Panel</h1>
        </div>
        <button onClick={() => navigate('/dashboard')} className="btn-secondary">
          <ArrowLeft size={18} />
          Back to Dashboard
        </button>
      </header>

      <div className="admin-tabs">
        <button className={activeTab === 'orders' ? 'active' : ''} onClick={() => setActiveTab('orders')}>
          <Package size={18} />
          Orders
        </button>
        <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>
          <Users size={18} />
          Users
        </button>
        <button className={activeTab === 'images' ? 'active' : ''} onClick={() => setActiveTab('images')}>
          <Image size={18} />
          Vessel Images
        </button>
      </div>

      <main className="admin-main">
        {activeTab === 'orders' && (
          <div className="card">
            <h2>Order Management</h2>
            <form onSubmit={handleOrderSubmit} className="admin-form">
              <input placeholder="Week (auto-filled from ETD)" value={orderForm.week} onChange={(e) => handleOrderFormChange('week', e.target.value)} readOnly style={{backgroundColor: '#1a1f36'}} />
              <input placeholder="Client" value={orderForm.client} onChange={(e) => handleOrderFormChange('client', e.target.value)} required />
              <input placeholder="Product" value={orderForm.product} onChange={(e) => handleOrderFormChange('product', e.target.value)} required />
              <input placeholder="Variety" value={orderForm.variety} onChange={(e) => handleOrderFormChange('variety', e.target.value)} required />
              <input type="number" placeholder="Pallets" value={orderForm.pallets} onChange={(e) => handleOrderFormChange('pallets', e.target.value)} required />
              <input placeholder="Load Date" value={orderForm.loadDate} onChange={(e) => handleOrderFormChange('loadDate', e.target.value)} />
              <input placeholder="Vessel" value={orderForm.vessel} onChange={(e) => handleOrderFormChange('vessel', e.target.value)} required />
              <input type="date" placeholder="ETD" value={orderForm.etd} onChange={(e) => handleOrderFormChange('etd', e.target.value)} required />
              <input type="date" placeholder="ETA" value={orderForm.eta} onChange={(e) => handleOrderFormChange('eta', e.target.value)} required />
              <select value={orderForm.status} onChange={(e) => handleOrderFormChange('status', e.target.value)}>
                <option>Planned</option>
                <option>Loaded</option>
                <option>Delayed</option>
              </select>
              <button type="submit" className="btn-primary">
                <Plus size={18} />
                Add Order
              </button>
            </form>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Week</th><th>Client</th><th>Product</th><th>Variety</th><th>Pallets</th><th>Vessel</th><th>ETD</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td>{o.week}</td><td>{o.client}</td><td>{o.product}</td><td>{o.variety}</td><td>{o.pallets}</td><td>{o.vessel}</td><td>{o.etd}</td><td>{o.status}</td>
                      <td><button onClick={() => handleDeleteOrder(o.id)} className="btn-danger"><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="card">
            <h2>User Management</h2>
            <form onSubmit={handleUserSubmit} className="admin-form">
              <input type="email" placeholder="Email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required />
              <input placeholder="Name" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required />
              <input placeholder="Client Name" value={userForm.clientName} onChange={(e) => setUserForm({ ...userForm, clientName: e.target.value })} required />
              <button type="submit" className="btn-primary">
                <Plus size={18} />
                Add User
              </button>
            </form>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Email</th><th>Name</th><th>Client</th><th>Approved</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id || u.email}>
                      <td>{u.email}</td><td>{u.name}</td><td>{u.client_name || u.clientName}</td>
                      <td>{u.approved ? <CheckCircle size={18} color="green" /> : <XCircle size={18} color="red" />}</td>
                      <td>
                        <button onClick={() => handleToggleApproval(u.id, u.approved)} className="btn-secondary" style={{ marginRight: '8px' }}>
                          {u.approved ? 'Revoke' : 'Approve'}
                        </button>
                        <button onClick={() => handleDeleteUser(u.id)} className="btn-danger">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'images' && (
          <div className="card">
            <h2>Vessel Brand Images</h2>
            <div className="image-upload-section">
              <select value={selectedVessel} onChange={(e) => setSelectedVessel(e.target.value)}>
                {vessels.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
            </div>
            <div className="image-grid">
              {(brandImages[selectedVessel] || []).map((img) => (
                <div key={img.id} className="image-item">
                  <img src={img.image_data} alt={`${selectedVessel} ${img.id}`} />
                  <button onClick={() => handleDeleteImage(img.id, selectedVessel)} className="btn-danger">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {(!brandImages[selectedVessel] || brandImages[selectedVessel].length === 0) && (
                <p>No images uploaded for this vessel.</p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
